/**
 * FleetManager Onboarding Wizard v2
 *
 * "Lemonade-style" light-theme conversational onboarding.
 * Captures real fleet data (vehicles, drivers, tools) instead of abstract counts.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2, Truck, Users, Shield, MapPin, CheckCircle2,
  ChevronRight, ChevronLeft, Sparkles, X, Loader2, RotateCcw, Play,
  Wrench
} from 'lucide-react';

import { onboardingAPI } from '../../../api.js';

// Step components
import WelcomeStep from './steps/WelcomeStep.jsx';
import SitesStep from './steps/SitesStep.jsx';
import InsuranceStep from './steps/InsuranceStep.jsx';
import FleetSizeStep from './steps/FleetSizeStep.jsx';
import VehicleEntryStep from './steps/VehicleEntryStep.jsx';
import DriversStep from './steps/DriversStep.jsx';
import MaterialsStep from './steps/MaterialsStep.jsx';
import SummaryStep from './steps/SummaryStep.jsx';
import { useFormat } from '../../../i18n/format.js';

// localStorage key for saving wizard progress
const STORAGE_KEY = 'fleetmanager_onboarding_v2';

const STEPS = [
  { id: 'welcome', label: 'Bienvenue', icon: Sparkles },
  { id: 'sites', label: 'Sites', icon: MapPin },
  { id: 'insurance', label: 'Assurance', icon: Shield },
  { id: 'vehicle_categories', label: 'Catégories', icon: Truck },
  { id: 'vehicle_entry', label: 'Véhicules', icon: Truck },
  { id: 'drivers', label: 'Conducteurs', icon: Users },
  { id: 'materials', label: 'Matériel', icon: Wrench },
  { id: 'summary', label: 'Récapitulatif', icon: CheckCircle2 },
];

// Default wizard state
const DEFAULT_WIZARD_DATA = {
  // Step 0: Welcome
  companyName: '',
  siret: '',
  sector: '',
  companyAddress: '',
  companyCity: '',
  companyPostalCode: '',

  // Step 1: Sites
  sites: [],

  // Step 2: Insurance
  hasFleetContract: null,
  insurerName: '',
  brokerName: '',
  policyNumber: '',

  // Step 3: Vehicle Categories (counter cards)
  vehicleCategories: {
    VL: 0,
    VU: 0,
    PL: 0,
    ENGIN_CHANTIER: 0,
    MOTO: 0,
  },

  // Step 4: Vehicles (real vehicle entries per category)
  vehicles: [],

  // Step 5: Drivers (real driver entries)
  drivers: [],
  certifications: [],

  // Step 6: Materials/Tools
  hasMaterials: null,
  materials: [],

  // Legacy compat (used by backend)
  fleetSize: 'small',
  vehicleTypes: { light: 0, utility: 0, heavy: 0, special: 0 },
  driverCount: 0,
};

export function OnboardingWizard({ onComplete, onSkip, tenant }) {
  const { fmt } = useFormat();
  const [currentStep, setCurrentStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null);

  // Pre-fill wizard data from tenant (cockpit commercial data)
  const initialData = React.useMemo(() => {
    if (!tenant) return DEFAULT_WIZARD_DATA;
    return {
      ...DEFAULT_WIZARD_DATA,
      companyName: tenant.corporate_name || tenant.name || '',
      siret: tenant.siret || '',
      sector: '', // Not stored on tenant yet, user picks
      companyAddress: tenant.address_street || '',
      companyCity: tenant.address_city || '',
      companyPostalCode: tenant.address_postal_code || '',
    };
  }, [tenant]);

  // Wizard state - collected data
  const [wizardData, setWizardData] = useState(initialData);

  // Load saved progress on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const savedDate = new Date(parsed.savedAt);
        const now = new Date();
        const daysDiff = (now - savedDate) / (1000 * 60 * 60 * 24);

        if (daysDiff < 7 && parsed.currentStep > 0) {
          setSavedProgress(parsed);
          setShowResumeDialog(true);
        } else if (daysDiff >= 7) {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (err) {
      console.error('[Onboarding] Error loading saved progress:', err);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Save progress to localStorage when step or data changes
  useEffect(() => {
    if (currentStep === 0 || showResumeDialog) return;

    try {
      const saveData = {
        currentStep,
        wizardData,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(saveData));
    } catch (err) {
      console.error('[Onboarding] Error saving progress:', err);
    }
  }, [currentStep, wizardData, showResumeDialog]);

  const clearSavedProgress = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setSavedProgress(null);
  }, []);

  const handleResume = useCallback(() => {
    if (savedProgress) {
      setCurrentStep(savedProgress.currentStep);
      setWizardData(prev => ({ ...initialData, ...savedProgress.wizardData }));
    }
    setShowResumeDialog(false);
  }, [savedProgress, initialData]);

  const handleStartFresh = useCallback(() => {
    clearSavedProgress();
    setWizardData(initialData);
    setShowResumeDialog(false);
  }, [clearSavedProgress, initialData]);

  const updateData = useCallback((updates) => {
    setWizardData(prev => ({ ...prev, ...updates }));
    setError(null);
  }, []);

  // Navigation
  const goNext = useCallback(() => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep]);

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback((stepIndex) => {
    if (stepIndex >= 0 && stepIndex < STEPS.length) {
      setCurrentStep(stepIndex);
    }
  }, []);

  const skipStep = useCallback(() => {
    goNext();
  }, [goNext]);

  // Generate fleet
  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    setError(null);

    try {
      // Map vehicleCategories to legacy vehicleTypes for backend compat
      const cats = wizardData.vehicleCategories || {};
      const legacyVehicleTypes = {
        light: cats.VL || 0,
        utility: cats.VU || 0,
        heavy: cats.PL || 0,
        special: cats.ENGIN_CHANTIER || 0,
      };

      const config = {
        companyName: wizardData.companyName || 'Ma Flotte',
        siret: wizardData.siret,
        sector: wizardData.sector,
        companyAddress: wizardData.companyAddress,
        companyCity: wizardData.companyCity,
        companyPostalCode: wizardData.companyPostalCode,
        fleetSize: wizardData.fleetSize,
        vehicleTypes: legacyVehicleTypes,
        vehicles: wizardData.vehicles || [],
        // Real driver data
        drivers: wizardData.drivers || [],
        driverCount: (wizardData.drivers || []).length || wizardData.driverCount,
        certifications: wizardData.certifications,
        insurerName: wizardData.insurerName,
        brokerName: wizardData.brokerName,
        policyNumber: wizardData.policyNumber,
        sites: wizardData.sites,
        // Materials/tools
        materials: wizardData.materials || [],
        generateSampleData: false,
      };

      const response = await onboardingAPI.generate(config);

      if (response.ok) {
        clearSavedProgress();
        onComplete?.(response.data);
      } else {
        setError(response.error || 'Erreur lors de la génération');
      }
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.message || 'Erreur inattendue');
    } finally {
      setIsGenerating(false);
    }
  }, [wizardData, onComplete, clearSavedProgress]);

  const handleSkip = useCallback(() => {
    clearSavedProgress();
    onSkip?.();
  }, [clearSavedProgress, onSkip]);

  // Render current step
  const renderStep = () => {
    const stepProps = {
      data: wizardData,
      updateData,
      onNext: goNext,
      onBack: goBack,
      onSkip: skipStep,
    };

    switch (STEPS[currentStep].id) {
      case 'welcome':
        return <WelcomeStep {...stepProps} />;
      case 'sites':
        return <SitesStep {...stepProps} />;
      case 'insurance':
        return <InsuranceStep {...stepProps} />;
      case 'vehicle_categories':
        return <FleetSizeStep {...stepProps} />;
      case 'vehicle_entry':
        return <VehicleEntryStep {...stepProps} />;
      case 'drivers':
        return <DriversStep {...stepProps} />;
      case 'materials':
        return <MaterialsStep {...stepProps} />;
      case 'summary':
        return (
          <SummaryStep
            {...stepProps}
            onGenerate={handleGenerate}
            isGenerating={isGenerating}
            error={error}
            goToStep={goToStep}
          />
        );
      default:
        return null;
    }
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  return (
    <div className="fixed inset-0 bg-gray-50 flex flex-col z-50">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
            <Truck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">FleetManager</h1>
            <p className="text-sm text-gray-500">Configuration initiale</p>
          </div>
        </div>

        <button
          onClick={handleSkip}
          className="text-gray-400 hover:text-gray-700 transition-colors flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100"
        >
          <span>Passer</span>
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Resume dialog */}
      {showResumeDialog && savedProgress && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-200">
            <div className="p-6">
              <div className="w-14 h-14 bg-amber-50 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <RotateCcw className="w-7 h-7 text-amber-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                Configuration en cours
              </h3>
              <p className="text-gray-500 text-center mb-6">
                Vous aviez commencé une configuration le{' '}
                <span className="text-gray-900 font-medium">
                  {fmt.date(savedProgress.savedAt, {
                    day: 'numeric',
                    month: 'long',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
                . Voulez-vous reprendre ou recommencer ?
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-500">Progression</span>
                  <span className="text-amber-600 font-medium">
                    {STEPS[savedProgress.currentStep]?.label} ({savedProgress.currentStep + 1}/{STEPS.length})
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500"
                    style={{ width: `${((savedProgress.currentStep + 1) / STEPS.length) * 100}%` }}
                  />
                </div>
                {savedProgress.wizardData?.companyName && (
                  <div className="mt-3 text-sm text-gray-600">
                    Entreprise: <span className="text-gray-900">{savedProgress.wizardData.companyName}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleStartFresh}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  <X className="w-4 h-4" />
                  <span>Recommencer</span>
                </button>
                <button
                  onClick={handleResume}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 hover:bg-amber-400 text-white rounded-lg font-medium transition-colors"
                >
                  <Play className="w-4 h-4" />
                  <span>Reprendre</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div className="px-6 py-3 bg-white">
        <div className="flex items-center gap-2 mb-2">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;

            return (
              <button
                key={step.id}
                onClick={() => goToStep(index)}
                disabled={index > currentStep}
                className={`
                  flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all
                  ${isActive ? 'bg-amber-500 text-white' : ''}
                  ${isCompleted ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : ''}
                  ${!isActive && !isCompleted ? 'bg-gray-100 text-gray-400' : ''}
                  ${index > currentStep ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                `}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{step.label}</span>
              </button>
            );
          })}
        </div>
        <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Step content — wider for vehicle/driver/material table steps */}
      <div className="flex-1 overflow-y-auto">
        <div className={`mx-auto px-6 py-8 ${
          ['vehicle_entry', 'drivers', 'materials'].includes(STEPS[currentStep]?.id)
            ? 'max-w-5xl'
            : 'max-w-2xl'
        }`}>
          {renderStep()}
        </div>
      </div>

      {/* Footer with navigation */}
      <div className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button
            onClick={goBack}
            disabled={currentStep === 0}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all
              ${currentStep === 0
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }
            `}
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Retour</span>
          </button>

          <span className="text-gray-400 text-sm">
            Étape {currentStep + 1} sur {STEPS.length}
          </span>

          {currentStep < STEPS.length - 1 ? (
            <button
              onClick={goNext}
              className="flex items-center gap-2 px-6 py-2 bg-amber-500 hover:bg-amber-400 text-white rounded-lg font-medium transition-all shadow-sm"
            >
              <span>Continuer</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium transition-all shadow-sm disabled:opacity-50"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Génération...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  <span>Activer ma flotte !</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default OnboardingWizard;
